import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DocumentViewerProps {
  isOpen: boolean;
  onClose: () => void;
  documentUrl: string;
  title: string;
}

/**
 * 文档查看器组件
 * 用于在应用内显示隐私政策、服务条款等HTML文档
 */
export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  isOpen,
  onClose,
  documentUrl,
  title,
}) => {
  const { t } = useTranslation(['v3/common']);
  const [documentContent, setDocumentContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(null);
      
      // 获取文档内容
      fetch(documentUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.text();
        })
        .then(html => {
          // 提取文档的 body 内容
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const bodyContent = doc.body.innerHTML;
          setDocumentContent(bodyContent);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error loading document:', err);
          setError(t('v3/common:errors.documentLoadFailed'));
          setLoading(false);
        });
    }
  }, [isOpen, documentUrl, t]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 font-ios">
      <div className="relative w-full max-w-lg max-h-[90vh] bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden shadow-ios dark:shadow-ios-dark">
        {/* 标题栏 */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-white dark:bg-ios-dark-gray6 border-b border-ios-gray5 dark:border-ios-dark-gray5">
          <h2 className="text-ios-title3 font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 text-ios-gray dark:text-ios-dark-gray rounded-full hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray5 active:bg-ios-gray5 dark:active:bg-ios-dark-gray4 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        {/* 内容区域 */}
        <div className="overflow-y-auto p-4 max-h-[calc(90vh-4rem)] pb-20">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="w-8 h-8 border-4 border-ios-blue dark:border-ios-dark-blue border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <div className="text-ios-red dark:text-ios-dark-red text-center py-8">
              {error}
            </div>
          ) : (
            <div 
              className="document-content"
              dangerouslySetInnerHTML={{ __html: documentContent }}
            />
          )}
        </div>
        
        {/* 底部按钮 */}
        <div className="sticky bottom-0 z-10 flex justify-end p-4 bg-white dark:bg-ios-dark-gray6 border-t border-ios-gray5 dark:border-ios-dark-gray5">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-ios-blue dark:bg-ios-dark-blue text-white font-medium rounded-ios-lg"
          >
            {t('v3/common:actions.close')}
          </button>
        </div>
      </div>
    </div>
  );
};
